import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import api from '../api';
import { ONBOARDING_DOCUMENT_TYPES } from '../constants/offer';
import { uploadUrl } from '../utils/uploads';

const STATUS_STYLES = {
  pending: 'bg-[hsl(var(--ds-warning)/0.15)] text-[hsl(var(--ds-warning-fg))]',
  approved: 'bg-[hsl(var(--ds-success)/0.12)] text-[hsl(var(--ds-success))]',
  rejected: 'bg-[hsl(var(--ds-danger)/0.12)] text-[hsl(var(--ds-danger))]'
};

function formatWhen(iso) {
  if (!iso) return '';
  return new Date(iso).toLocaleString();
}

export default function OnboardingWorkflowPanel({ app, userRole, onUpdated }) {
  const qc = useQueryClient();
  const isCandidate = userRole === 'candidate';
  const isStaff = userRole === 'admin' || userRole === 'recruiter' || userRole === 'client';

  const { data: meta } = useQuery({
    queryKey: ['hiring-meta'],
    queryFn: () => api.get('/meta/hiring').then(r => r.data),
    staleTime: 60000
  });

  const approvalTypes = meta?.onboardingApprovalTypes || [];

  const [uploadCategory, setUploadCategory] = useState('aadhar');
  const [uploadTitle, setUploadTitle] = useState('');
  const [uploadNote, setUploadNote] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const [approvalForm, setApprovalForm] = useState({
    approvalType: 'hr',
    title: '',
    description: ''
  });
  const [decision, setDecision] = useState({ itemId: null, status: 'approved', comment: '' });

  const items = app.onboardingItems || [];
  const progress = app.documentsProgress;

  const refresh = data => {
    qc.invalidateQueries({ queryKey: ['myApplications'] });
    qc.invalidateQueries({ queryKey: ['applications'] });
    onUpdated?.(data);
  };

  const uploadMutation = useMutation({
    mutationFn: async files => {
      const form = new FormData();
      for (const f of files) form.append('files', f);
      form.append('type', uploadCategory);
      if (uploadTitle.trim()) form.append('title', uploadTitle.trim());
      if (uploadNote.trim()) form.append('description', uploadNote.trim());
      return api.post(`/applications/${app.id}/documents`, form).then(r => r.data);
    },
    onSuccess: data => {
      setError('');
      setUploading(false);
      setUploadTitle('');
      setUploadNote('');
      refresh(data);
    },
    onError: err => {
      setUploading(false);
      setError(err.response?.data?.message || 'Upload failed');
    }
  });

  const createApprovalMutation = useMutation({
    mutationFn: payload =>
      api.post(`/applications/${app.id}/onboarding-approvals`, payload).then(r => r.data),
    onSuccess: refresh,
    onError: err => setError(err.response?.data?.message || 'Could not add approval step')
  });

  const recordApprovalMutation = useMutation({
    mutationFn: ({ itemId, status, comment }) =>
      api
        .post(`/applications/${app.id}/onboarding-items/${itemId}/approve`, { status, comment })
        .then(r => r.data),
    onSuccess: data => {
      setDecision({ itemId: null, status: 'approved', comment: '' });
      refresh(data);
    },
    onError: err => setError(err.response?.data?.message || 'Approval failed')
  });

  const handleMultiUpload = e => {
    const fileList = [...(e.target.files || [])];
    if (!fileList.length) return;
    setError('');
    setUploading(true);
    uploadMutation.mutate(fileList);
    e.target.value = '';
  };

  return (
    <div className="rounded-xl border border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] shadow-[var(--ds-shadow-card)] p-4 space-y-4">
      <div>
        <p className="font-medium text-[hsl(var(--ds-fg))]">Offer → joining: documents & approvals</p>
        <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-0.5">
          Upload multiple files per category. Staff can add approval checkpoints and record who approved and when.
        </p>
        {progress && (
          <p className="text-xs text-[hsl(var(--ds-success))] mt-1 font-medium">
            Documents {progress.uploadedCount}/{progress.requiredCount} required · {progress.totalFiles} file(s) ·{' '}
            Approvals {progress.approvalsComplete}/{progress.approvalCheckpoints} complete
            {progress.pendingApprovals > 0 ? ` · ${progress.pendingApprovals} pending` : ''}
          </p>
        )}
      </div>

      {error && <p className="text-xs text-red-600">{error}</p>}

      {(isCandidate || isStaff) && (
        <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] p-3 space-y-2">
          <p className="text-xs font-medium text-[hsl(var(--ds-fg))]">Upload document(s)</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <div className="field-with-label">
              <select
                className="border border-[hsl(var(--ds-border))] px-2 py-2 text-sm w-full"
                value={uploadCategory}
                onChange={e => setUploadCategory(e.target.value)}
              >
                {ONBOARDING_DOCUMENT_TYPES.map(d => (
                  <option key={d.code} value={d.code}>
                    {d.label}
                  </option>
                ))}
              </select>
              <label>Category</label>
            </div>
            <div className="field-with-label">
              <input
                className="border border-[hsl(var(--ds-border))] px-2 py-2 text-sm w-full"
                placeholder=" "
                value={uploadTitle}
                onChange={e => setUploadTitle(e.target.value)}
              />
              <label>Title (optional)</label>
            </div>
          </div>
          <div className="field-with-label">
            <input
              className="border border-[hsl(var(--ds-border))] px-2 py-2 text-sm w-full"
              placeholder=" "
              value={uploadNote}
              onChange={e => setUploadNote(e.target.value)}
            />
            <label>Note (optional)</label>
          </div>
          <label className="inline-block text-xs border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-3 py-2 cursor-pointer hover:bg-[hsl(var(--ds-primary)/0.08)]">
            {uploading ? 'Uploading…' : 'Choose file(s) — multiple allowed'}
            <input
              type="file"
              className="hidden"
              multiple
              accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
              disabled={uploading}
              onChange={handleMultiUpload}
            />
          </label>
        </div>
      )}

      {isStaff && (
        <div className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] p-3 space-y-2">
          <p className="text-xs font-medium text-[hsl(var(--ds-fg))]">Add approval checkpoint</p>
          <div className="grid gap-2 sm:grid-cols-2">
            <select
              className="border border-[hsl(var(--ds-border))] px-2 py-2 text-sm"
              value={approvalForm.approvalType}
              onChange={e => setApprovalForm(f => ({ ...f, approvalType: e.target.value }))}
            >
              {approvalTypes.map(t => (
                <option key={t.code} value={t.code}>
                  {t.label}
                </option>
              ))}
            </select>
            <input
              className="border border-[hsl(var(--ds-border))] px-2 py-2 text-sm"
              placeholder="Custom title (optional)"
              value={approvalForm.title}
              onChange={e => setApprovalForm(f => ({ ...f, title: e.target.value }))}
            />
          </div>
          <input
            className="border border-[hsl(var(--ds-border))] px-2 py-2 text-sm w-full"
            placeholder="Instructions for this approval step"
            value={approvalForm.description}
            onChange={e => setApprovalForm(f => ({ ...f, description: e.target.value }))}
          />
          <button
            type="button"
            className="text-xs border border-[hsl(var(--ds-border))] px-3 py-1.5 hover:bg-[hsl(var(--ds-muted))]"
            disabled={createApprovalMutation.isLoading}
            onClick={() => createApprovalMutation.mutate(approvalForm)}
          >
            {createApprovalMutation.isLoading ? 'Adding…' : 'Add approval step'}
          </button>
        </div>
      )}

      <div className="space-y-3">
        {items.length === 0 && (
          <p className="text-xs text-[hsl(var(--ds-muted-fg))]">No documents or approvals yet.</p>
        )}
        {items.map(item => (
          <div key={item.id} className="bg-[hsl(var(--ds-surface))] border border-[hsl(var(--ds-border))] p-3 text-sm space-y-2">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div>
                <p className="font-medium text-[hsl(var(--ds-fg))]">
                  {item.title}
                  {item.kind === 'approval' && (
                    <span className="ml-1 text-[10px] font-normal text-[hsl(var(--ds-muted-fg))]">(approval)</span>
                  )}
                </p>
                {item.description && <p className="text-xs text-[hsl(var(--ds-muted-fg))] mt-0.5">{item.description}</p>}
              </div>
              <span className={`text-[10px] px-2 py-0.5 capitalize ${STATUS_STYLES[item.status] || STATUS_STYLES.pending}`}>
                {item.status || 'pending'}
              </span>
            </div>

            {(item.files || []).length > 0 && (
              <ul className="text-xs space-y-1 border-t border-[hsl(var(--ds-border))] pt-2">
                {item.files.map(f => (
                  <li key={f.id} className="flex flex-wrap justify-between gap-2">
                    <span className="text-[hsl(var(--ds-muted-fg))]">
                      {f.originalName || 'File'} · {f.uploadedByName || 'User'} · {formatWhen(f.uploadedAt)}
                    </span>
                    <a
                      href={uploadUrl(f.fileUrl)}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-[hsl(var(--ds-primary))] hover:underline shrink-0"
                    >
                      View
                    </a>
                  </li>
                ))}
              </ul>
            )}

            {(item.approvals || []).length > 0 && (
              <div className="border-t border-[hsl(var(--ds-border))] pt-2 space-y-1">
                <p className="text-[10px] font-medium text-[hsl(var(--ds-muted-fg))] uppercase tracking-wide">Approval history</p>
                {item.approvals.map(a => (
                  <div key={a.id} className="text-xs bg-[hsl(var(--ds-muted))] px-2 py-1.5 border border-[hsl(var(--ds-border))]">
                    <span className="font-medium capitalize">{a.status}</span>
                    {' · '}
                    <span>{a.byName}</span>
                    {a.byRole && <span className="text-[hsl(var(--ds-muted-fg))]"> ({a.byRole})</span>}
                    {' · '}
                    <span className="text-[hsl(var(--ds-muted-fg))]">{formatWhen(a.at)}</span>
                    {a.comment && <p className="text-[hsl(var(--ds-fg))] mt-0.5">{a.comment}</p>}
                  </div>
                ))}
              </div>
            )}

            {isStaff && (
              <div className="border-t border-[hsl(var(--ds-border))] pt-2 flex flex-wrap gap-2 items-end">
                {decision.itemId === item.id ? (
                  <div className="w-full space-y-2">
                    <select
                      className="border border-[hsl(var(--ds-border))] px-2 py-1 text-xs"
                      value={decision.status}
                      onChange={e => setDecision(d => ({ ...d, status: e.target.value }))}
                    >
                      <option value="approved">Approve</option>
                      <option value="rejected">Reject</option>
                    </select>
                    <input
                      className="border border-[hsl(var(--ds-border))] px-2 py-1 text-xs w-full"
                      placeholder="Comment (optional)"
                      value={decision.comment}
                      onChange={e => setDecision(d => ({ ...d, comment: e.target.value }))}
                    />
                    <div className="flex gap-2">
                      <button
                        type="button"
                        className="text-xs border border-[hsl(var(--ds-primary))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-primary))] hover:bg-[hsl(var(--ds-primary)/0.08)] hover:border-[hsl(var(--ds-primary-hover))] hover:text-[hsl(var(--ds-primary-hover))] px-2 py-1"
                        disabled={recordApprovalMutation.isLoading}
                        onClick={() =>
                          recordApprovalMutation.mutate({
                            itemId: item.id,
                            status: decision.status,
                            comment: decision.comment
                          })
                        }
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="text-xs border border-[hsl(var(--ds-border))] px-2 py-1"
                        onClick={() => setDecision({ itemId: null, status: 'approved', comment: '' })}
                      >
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="text-xs text-[hsl(var(--ds-primary))] hover:underline"
                    onClick={() => setDecision({ itemId: item.id, status: 'approved', comment: '' })}
                  >
                    Record approval / rejection
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
